/**
 * Gli stati non arrivano tutti dalla stessa penna.
 *
 * Installazioni, Attivazioni e Sopralluoghi hanno liste chiuse scritte
 * nell'app, quindi i loro valori sono già canonici. **Interventi no**: lo
 * `stato` di un intervento è il NOME DELLO STAGE configurato in Odoo, copiato
 * alla lettera (`routes/interventions.ts`, `stage_id[1]`), e arriva da due
 * pipeline distinte — Helpdesk e Field Service — con liste separate mantenute
 * in un altro sistema. In produzione convivono `"Programmato"` e
 * `"da Programmare"`.
 *
 * Rinominare quei valori nel database non regge: la sync riscrive i campi
 * remoti a ogni giro e `stato` è fra quelli controllati, quindi un UPDATE
 * tornerebbe indietro al primo allineamento lasciando solo una riga di
 * discrepanza nel log. Il posto dove si rinomina uno stage è Odoo.
 *
 * Quindi si unifica la LETTURA, non il dato: il testo mostrato resta quello
 * vero, il colore e il raggruppamento passano da una chiave normalizzata.
 *
 * ATTENZIONE — `"da Programmare"` e `"Programmato"` NON sono due grafie dello
 * stesso stato: sono due stati diversi. "Da programmare" vuol dire che nessuno
 * l'ha ancora messo in calendario, "Programmato" che una data c'è. Sono
 * esattamente i due che il rosso e il blu separano. Qui restano distinti di
 * proposito.
 */

/**
 * Chiave di raggruppamento di uno stato. Normalizza tre cose, e nient'altro:
 *
 * 1. **spazi** — `" Programmato "` e `"Programmato"` sono lo stesso stato;
 * 2. **maiuscole** — `"da Programmare"` e `"Da programmare"` idem;
 * 3. **genere** — un participio che finisce in `-to/-ta` o `-so/-sa` perde la
 *    vocale finale, così `"Programmato"` (intervento, maschile) e
 *    `"Programmata"` (installazione, femminile) cadono sulla stessa chiave.
 *    È l'unico modo per far combaciare moduli i cui nomi hanno genere diverso
 *    senza forzare "Intervento: Programmata", che in italiano è sbagliato.
 *
 * Non normalizza i sinonimi: quelli stanno in SINONIMI, esplicitati uno per
 * uno, perché dedurli sarebbe indovinare.
 */
export function chiaveStato(stato: string | null | undefined): string | null {
  if (!stato) return null;
  const normale = stato
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/([ts])[oa]$/, "$1"); // participio: -to/-ta, -so/-sa → -t, -s
  if (normale === "") return null;
  return SINONIMI[normale] ?? normale;
}

/**
 * Parole diverse che indicano lo stesso stato. Solo quelle **osservate nei
 * dati reali**: dedurne altre a tavolino significherebbe accorpare stati che
 * in Odoo qualcuno ha voluto distinti.
 *
 * `completat` ← "Completato", lo stage con cui Odoo chiude un intervento: è
 * la stessa cosa che l'app chiama "Effettuata" su un'installazione, e senza
 * questa riga sarebbe l'unico stato concluso a non leggersi come concluso.
 *
 * Candidati NON inseriti perché non li ho visti nei dati: "Risolto" (stage
 * standard di Odoo Helpdesk), "Chiuso", "Annullato". Si aggiungono qui quando
 * compaiono davvero.
 */
const SINONIMI: Record<string, string> = {
  completat: "effettuat",
};

/**
 * Costruisce un indice canonico da una mappa scritta con le etichette umane
 * (`STATO_HEX`, `STATO_STYLES`), così le due tabelle dei colori restano
 * leggibili — `Effettuata: "#22c55e"` — e la tolleranza si aggiunge sopra
 * senza doverle riscrivere a chiavi normalizzate.
 */
export function indicizzaPerChiave<T>(perEtichetta: Record<string, T>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [etichetta, valore] of Object.entries(perEtichetta)) {
    const chiave = chiaveStato(etichetta);
    if (chiave) out[chiave] = valore;
  }
  return out;
}

/** Cerca in una mappa scritta per etichette usando la chiave normalizzata. */
export function perStatoTollerante<T>(indice: Record<string, T>, stato: string | null | undefined): T | undefined {
  const chiave = chiaveStato(stato);
  return chiave ? indice[chiave] : undefined;
}
